import React from 'react';
import { useForm, usePage } from '@inertiajs/react';
import { DitherBackground } from '@/frontend/components/DitherBackground';

interface Props {
  token: string;
  errors?: string[];
}

export default function PasswordEdit({ token, errors: serverErrors }: Props) {
  const { props } = usePage<{ flash: { alert?: string } }>();
  const { data, setData, put, processing } = useForm({
    password: '',
    password_confirmation: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    put(`/passwords/${token}`);
  };

  // bg-white (not bg-background): this page is a fixed-light design; the theme
  // class is applied from auth (SZ-1582) and would flip the token under dark.
  const inputClass = 'w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400 transition-all';

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden" style={{ background: '#f8f7ff' }}>
      {/* Glass navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-4 pt-4">
        <div
          className="max-w-[1200px] mx-auto px-6 h-14 flex items-center justify-between rounded-xl"
          style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.4) 100%)',
            backdropFilter: 'blur(24px) saturate(1.8)',
            WebkitBackdropFilter: 'blur(24px) saturate(1.8)',
            border: '1px solid rgba(255,255,255,0.5)',
            boxShadow: '0 1px 2px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -1px 0 rgba(255,255,255,0.2)',
          }}
        >
          <a href="/" className="flex items-center">
            <img src="/landing/images/sz-orb-logo.png" alt="StationZero" className="h-6 object-contain" />
          </a>
        </div>
      </nav>

      {/* Dither background */}
      <DitherBackground />

      {/* Gradient glow */}
      <div
        className="absolute inset-0"
        style={{ zIndex: 1, background: 'radial-gradient(ellipse at center, rgba(124,58,237,0.2) 0%, transparent 60%)' }}
      />

      {/* Card */}
      <div className="relative z-10 w-full max-w-[440px] mx-auto px-6">
        <div
          className="px-6 py-10 md:px-10 md:py-12"
          style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0.5) 100%)',
            backdropFilter: 'blur(24px) saturate(1.8)',
            WebkitBackdropFilter: 'blur(24px) saturate(1.8)',
            border: '1px solid rgba(255,255,255,0.6)',
            borderRadius: '20px',
            boxShadow: '0 8px 40px rgba(124,58,237,0.08), inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(255,255,255,0.3)',
          }}
        >
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-slate-900">Definir nova senha</h1>
          </div>

          {/* Server errors */}
          {serverErrors && serverErrors.length > 0 && (
            <div className="mb-4 text-sm text-red-500 text-center">
              {serverErrors.join(', ')}
            </div>
          )}

          {props.flash?.alert && (
            <div className="mb-4 text-sm text-red-500 text-center">
              {props.flash.alert}
            </div>
          )}

          <form className="space-y-4" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1.5">
                Nova senha
              </label>
              <input
                id="password"
                name="password"
                type="password"
                value={data.password}
                onChange={(e) => setData('password', e.target.value)}
                autoFocus
                required
                className={inputClass}
              />
            </div>

            <div>
              <label htmlFor="password_confirmation" className="block text-sm font-medium text-slate-700 mb-1.5">
                Confirmar nova senha
              </label>
              <input
                id="password_confirmation"
                name="password_confirmation"
                type="password"
                value={data.password_confirmation}
                onChange={(e) => setData('password_confirmation', e.target.value)}
                required
                className={inputClass}
              />
            </div>

            <button
              type="submit"
              disabled={processing}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#7c3aed] hover:bg-[#8b5cf6] disabled:opacity-60 text-white text-sm font-medium rounded-lg transition-all hover:-translate-y-0.5 mt-2"
            >
              {processing ? (
                <>
                  <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                    <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
                  </svg>
                  Redefinindo...
                </>
              ) : (
                <>
                  Redefinir senha
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
                  </svg>
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
